// AssinaturasView — admin da organização vê todas as assinaturas
// recorrentes dos alunos e o status de cobrança.
//
// Top: cards de KPI (ativas, em atraso, total mensal recorrente).
// Filtros: status, tipo de cobrança (Pix mensal vs Cartão).
// Tabela: nome do aluno, pacote, valor, tipo, próxima cobrança, status,
// últimas 3 cobranças (com motivo de falha se aplicável).

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  Loader2,
  Pause,
  QrCode,
  RefreshCw,
  XCircle
} from "lucide-react";
import {
  api,
  ApiError,
  type SubscriptionAdminRow,
  type SubscriptionSummary
} from "../api";
import { friendlyError } from "@app/ui/lib/friendly-error";

const phase = signal<"loading" | "ready" | "error">("loading");
const items = signal<SubscriptionAdminRow[]>([]);
const summary = signal<SubscriptionSummary | null>(null);
const errMsg = signal<string | null>(null);
const statusFilter = signal<"all" | "active" | "past_due" | "paused">("all");
const billingFilter = signal<"all" | "pix_monthly" | "credit_card">("all");

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listSubscriptions({
      status: statusFilter.value === "all" ? undefined : statusFilter.value,
      billingType:
        billingFilter.value === "all" ? undefined : billingFilter.value
    });
    items.value = r.items;
    summary.value = r.summary;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

function brl(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

export function AssinaturasView() {
  useEffect(() => {
    void load();
  }, []);

  return (
    <div class="space-y-6">
      <header class="flex items-start justify-between gap-3">
        <div>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <CreditCard className="size-6 text-primary" />
            Assinaturas recorrentes
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Alunos com cobrança automática mensal (Pix ou cartão). O cron
            diário processa o que vence hoje ou antes.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={phase.value === "loading"}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
        >
          <RefreshCw
            className={`size-3.5 ${phase.value === "loading" ? "animate-spin" : ""}`}
          />
          Atualizar
        </button>
      </header>

      {summary.value && <KpiStrip s={summary.value} />}

      <FiltersBar />

      {phase.value === "loading" && (
        <p class="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </p>
      )}
      {phase.value === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
          <AlertCircle className="mr-2 inline size-4 text-destructive" />
          {errMsg.value}
        </div>
      )}
      {phase.value === "ready" && items.value.length === 0 && (
        <div class="rounded-md border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          Nenhuma assinatura recorrente ainda.
        </div>
      )}
      {phase.value === "ready" && items.value.length > 0 && (
        <SubscriptionsList rows={items.value} />
      )}
    </div>
  );
}

function KpiStrip({ s }: { s: SubscriptionSummary }) {
  return (
    <div class="grid gap-3 sm:grid-cols-4">
      <Kpi
        label="Ativas"
        value={s.active}
        accent="emerald"
        icon={CheckCircle2}
      />
      <Kpi
        label="Em atraso"
        value={s.pastDue}
        accent={s.pastDue > 0 ? "destructive" : "muted"}
        icon={AlertCircle}
      />
      <Kpi
        label="Pausadas"
        value={s.paused}
        accent="amber"
        icon={Pause}
      />
      <Kpi
        label="Total mensal"
        value={brl(s.totalMonthly)}
        accent="violet"
        icon={CreditCard}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  accent,
  icon: Icon
}: {
  label: string;
  value: string | number;
  accent: "emerald" | "destructive" | "amber" | "violet" | "muted";
  icon: typeof CreditCard;
}) {
  const accentClass = {
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300",
    destructive:
      "border-destructive/40 bg-destructive/5 text-destructive",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300",
    violet: "border-violet-500/30 bg-violet-500/5 text-violet-700 dark:text-violet-300",
    muted: "border-border bg-card/40 text-muted-foreground"
  }[accent];
  return (
    <div class={`rounded-md border p-4 ${accentClass}`}>
      <div class="flex items-center gap-2">
        <Icon className="size-4" />
        <span class="text-[10px] font-bold uppercase tracking-wider">
          {label}
        </span>
      </div>
      <p class="mt-1 text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

function FiltersBar() {
  return (
    <div class="flex flex-wrap items-center gap-2">
      <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Status
      </span>
      <FilterChip
        active={statusFilter.value === "all"}
        label="Todas"
        onClick={() => {
          statusFilter.value = "all";
          void load();
        }}
      />
      <FilterChip
        active={statusFilter.value === "active"}
        label="Ativas"
        onClick={() => {
          statusFilter.value = "active";
          void load();
        }}
      />
      <FilterChip
        active={statusFilter.value === "past_due"}
        label="Em atraso"
        onClick={() => {
          statusFilter.value = "past_due";
          void load();
        }}
      />
      <FilterChip
        active={statusFilter.value === "paused"}
        label="Pausadas"
        onClick={() => {
          statusFilter.value = "paused";
          void load();
        }}
      />
      <span class="mx-2 h-4 w-px bg-border" />
      <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Cobrança
      </span>
      <FilterChip
        active={billingFilter.value === "all"}
        label="Tudo"
        onClick={() => {
          billingFilter.value = "all";
          void load();
        }}
      />
      <FilterChip
        active={billingFilter.value === "pix_monthly"}
        label="Pix mensal"
        onClick={() => {
          billingFilter.value = "pix_monthly";
          void load();
        }}
      />
      <FilterChip
        active={billingFilter.value === "credit_card"}
        label="Cartão"
        onClick={() => {
          billingFilter.value = "credit_card";
          void load();
        }}
      />
    </div>
  );
}

function FilterChip({
  active,
  label,
  onClick
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background hover:border-primary/40 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

function SubscriptionsList({ rows }: { rows: SubscriptionAdminRow[] }) {
  return (
    <ul class="space-y-3">
      {rows.map((s) => (
        <SubscriptionRow key={s.id} sub={s} />
      ))}
    </ul>
  );
}

function SubscriptionRow({ sub }: { sub: SubscriptionAdminRow }) {
  const [expanded, setExpanded] = useState(false);
  const isPix = sub.billingType === "pix_monthly";

  return (
    <li class="rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        class="flex w-full items-start gap-3 p-4 text-left hover:bg-accent/30"
      >
        <div
          class={`grid size-10 shrink-0 place-items-center rounded-md ${
            isPix
              ? "bg-emerald-500/15 text-emerald-600"
              : "bg-violet-500/15 text-violet-600"
          }`}
        >
          {isPix ? <QrCode className="size-5" /> : <CreditCard className="size-5" />}
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-baseline gap-2">
            <span class="truncate font-medium">{sub.userName}</span>
            <span class="truncate text-xs text-muted-foreground">
              {sub.userEmail}
            </span>
          </div>
          <div class="mt-0.5 truncate text-xs text-muted-foreground">
            {sub.pacoteNomeSnap}
          </div>
          <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
            <span class="font-semibold tabular-nums">
              {brl(sub.valorCents)} /mês
            </span>
            <span class="text-muted-foreground">·</span>
            <span class="text-muted-foreground">
              {isPix ? "Pix mensal" : "Cartão"}
            </span>
            <span class="text-muted-foreground">·</span>
            <StatusBadge status={sub.status} />
            <span class="text-muted-foreground">·</span>
            <span class="text-muted-foreground">
              Próxima:{" "}
              <span class="font-medium text-foreground tabular-nums">
                {new Date(sub.nextChargeAt).toLocaleDateString("pt-BR")}
              </span>
            </span>
            {sub.failedAttempts > 0 && (
              <>
                <span class="text-muted-foreground">·</span>
                <span class="font-medium text-destructive">
                  {sub.failedAttempts} falha{sub.failedAttempts !== 1 ? "s" : ""}
                </span>
              </>
            )}
          </div>
        </div>
      </button>

      {expanded && sub.recentCharges.length > 0 && (
        <div class="border-t border-border bg-background/40 px-4 py-3">
          <div class="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Últimas cobranças
          </div>
          <ul class="space-y-1.5 text-xs">
            {sub.recentCharges.map((c) => (
              <li key={c.id} class="flex items-center gap-2">
                <ChargeIcon status={c.status} />
                <span class="text-muted-foreground tabular-nums">
                  {new Date(c.chargedAt).toLocaleString("pt-BR")}
                </span>
                <span class="font-medium tabular-nums">{brl(c.valorCents)}</span>
                {c.methodPriority !== null && (
                  <span class="text-muted-foreground">
                    ({c.methodPriority === 0 ? "principal" : "reserva"})
                  </span>
                )}
                {c.failureReason && (
                  <span class="ml-1 truncate text-amber-600">
                    {c.failureReason}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

function StatusBadge({ status }: { status: SubscriptionAdminRow["status"] }) {
  const map = {
    active: { label: "ativa", class: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
    paused: { label: "pausada", class: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
    past_due: { label: "em atraso", class: "bg-destructive/15 text-destructive" },
    canceled: { label: "cancelada", class: "bg-muted text-muted-foreground" }
  }[status];
  return (
    <span
      class={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${map.class}`}
    >
      {map.label}
    </span>
  );
}

function ChargeIcon({
  status
}: {
  status: SubscriptionAdminRow["recentCharges"][number]["status"];
}) {
  if (status === "succeeded")
    return <CheckCircle2 className="size-3.5 text-emerald-600" />;
  if (status === "failed") return <XCircle className="size-3.5 text-destructive" />;
  return <Loader2 className="size-3.5 text-muted-foreground" />;
}
